import {Cell} from 'notebook/tables/constants/CellConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';

/**
 * The seam between the generic transposed-table engine (`TransposedStructuredTableEditable`) and
 * table-type-specific knowledge (e.g. parameter confirmation). A pivot spec describes how to reshape
 * a flat structured table — whose rows secretly encode a 2D structure — into a transposed grid:
 * one canonical column is projected into the matrix cells, one row dimension becomes the grid rows,
 * and the other becomes the grid columns.
 *
 * Everything here is table-type-agnostic. All PMEI / parameter / specific-column knowledge lives in
 * the spec implementation (see `ParameterConfirmationTranspose`), never in the engine.
 */

/** How a single canonical row maps into the transposed grid. */
export interface TransposedRowClassification {
  /** Canonical rows sharing a rowKey collapse into one transposed grid row. */
  rowKey: string;
  /** Text shown in the frozen row-axis (left) column. */
  rowLabel: string;
  /**
   * Canonical rows sharing a columnKey collapse into one transposed grid column. Also identifies the
   * column for persisted UI state (e.g. widths), so prefer a run-stable value where sharing widths across
   * runs matters — the engine treats it purely as an opaque identity.
   */
  columnKey: string;
  /** Header text for the transposed column. Should be non-empty (supply a fallback). */
  columnLabel: string;
}

export interface TransposePivotSpec {
  /** provenanceUuid of the canonical column whose value is projected into the matrix cells. */
  projectedColumnUuid: string;
  /** Header title shown above the pivot grid. Falls back to the adapter's display name when omitted. */
  title?: string;
  /** Passed to `useHydratedStructuredTableData`; defaults to true when unset. */
  hasPrimarySamples?: boolean;
  /**
   * Given the hydrated canonical table, return a per-row classifier. Implementations should resolve
   * any needed column indices once here, then close over them in the returned function.
   */
  createRowClassifier: (table: Table) => (rowIndex: number) => TransposedRowClassification;
  /**
   * Namespace under which this table's column widths persist; tables returning the same key share widths
   * (keyed within it by each column's `columnKey`). Return null, or omit, for a per-table fallback.
   */
  getColumnWidthStoreKey?: (table: Table) => string | null;
}

/** Map each column's stable provenanceUuid to its index in the canonical (positional) cell grid. */
export function columnIndexByUuid(table: Table): Map<string, number> {
  const map = new Map<string, number>();
  table.columnConstraints.forEach((constraint, index) => {
    const uuid = constraint?.structuredTableColumn?.provenanceUuid;
    if (uuid != null) {
      map.set(uuid, index);
    }
  });
  return map;
}

/**
 * Read a cell's label text, for string-ish label columns (e.g. a name used as a row/column header).
 * `text` holds the already-formatted display value; fall back to a STRING typed value so both storage
 * shapes render. This is intentionally NOT a general typed-value formatter — for that, use
 * `formatTypedValue`.
 */
export function readCellLabelText(cell: Cell | undefined): string {
  if (cell == null) {
    return '';
  }
  if (cell.text != null && cell.text !== '') {
    return cell.text;
  }
  if (cell.typedValue?.type === BaseTypedValueType.STRING) {
    return cell.typedValue.rawValue;
  }
  return '';
}
